import uniqid from 'uniqid';
import isFn from './is-function';

const PROPERTY_COMPUTED = Symbol('Reactive.computed');
const PROPERTY_FILTER = Symbol('Reactive.filter');
const PROPERTY_STATE = Symbol('Reactive.state');
const PROPERTY_WATCH = Symbol('Reactive.watch');

export {
    PROPERTY_COMPUTED as computed,
    PROPERTY_FILTER as filter,
    PROPERTY_STATE as state,
    PROPERTY_WATCH as watch
}

const REACTIVE_PROPERTIES = [
    { SYM: PROPERTY_COMPUTED, DEF: 'computed'},
    { SYM: PROPERTY_FILTER, DEF: 'filter'},
    { SYM: PROPERTY_STATE, DEF: 'state'},
    { SYM: PROPERTY_WATCH, DEF: 'watch'}
];

const DEFAULT_DEFINITION = {
    computed: {},
    filter: {},
    state: {},
    watch: {}
};

class ReactiveComponent {
    #computedUpdateFn = null;
    #deferred = [];
    #filters = {};
    #state = {};
    #watch = {};

    get enumerable() {
        return true;
    }

    constructor(definition = DEFAULT_DEFINITION) {
        definition = { ...DEFAULT_DEFINITION, ...definition };

        REACTIVE_PROPERTIES.forEach(({ SYM, DEF }) => {
            const virtualDefinition = this[SYM]();
            this[SYM] = {
                ...virtualDefinition, // reactive definitions made on class instance
                ...definition[DEF] // definitions passed via constructor will take precedence since it's more explicit
            };
        });

        this.reactive(this[PROPERTY_STATE]);

        this.#defineComputed();
        this.#defineFilters();
        this.#defineWatchers();
    }
    
    #defineComputedDescriptor(fn, updateFn) {
        return {
            get: () => {
                this.#computedUpdateFn = updateFn;
                const value = fn();
                this.#computedUpdateFn = null;
                return value;
            }
        };
    }

    #defineComputed() {
        Object.entries(this[PROPERTY_COMPUTED]).forEach(([ prop, fn ]) => {
            if(isFn(fn)) {
                fn = fn.bind(this);

                const updateFn = () => {
                    const value = fn();
                    this.#triggerWatchers(prop, value);
                };

                const descriptor = this.#defineComputedDescriptor(fn, updateFn);
                Object.defineProperty(this, prop, descriptor);

                try { this[prop]; }
                catch(err) {
                    this.#deferred = [ ...this.#deferred, prop ];
                }
            }
        });
    }

    #defineFilters() {
        const filters = { ...this.#filters };

        Object.entries(this[PROPERTY_FILTER]).forEach(([ prop, fn ]) => {
            if(isFn(fn)) {
                filters[prop] = fn.bind(this);
            }
        });

        this.#filters = filters;
    }

    #defineStateDescriptor(prop) {
        const updates = [];

        return {
            enumerable: this.enumerable,
            get: () => {
                if(this.#computedUpdateFn && !updates.includes(this.#computedUpdateFn)) {
                    updates.push(this.#computedUpdateFn);
                }
        
                return this.#state[prop];
            },
            set: (newValue) => {
                const oldValue = this.#state[prop];
                const value = this.#filters[prop] ? this.#filters[prop](newValue, oldValue) : newValue;

                this.#state[prop] = value;
                this.#triggerWatchers(prop, value, oldValue);

                updates.forEach(updateFn => updateFn());
            }
        };
    }

    #defineState(prop) {
        const descriptor = this.#defineStateDescriptor(prop);
        Object.defineProperty(this, prop, descriptor);
    }

    #defineWatchers() {
        Object.entries(this[PROPERTY_WATCH]).forEach(([ prop, fn ]) => {
            if(isFn(fn)) {
                this.watch(prop, fn.bind(this));
            }
        });
    }

    #triggerWatchers(prop, newValue, oldValue) {
        const watchers = this.#watch[prop] ? Object.values(this.#watch[prop]) : [];
        watchers.forEach(fn => fn(newValue, oldValue));
    }

    #tryDeferred() {
        const deferred = [ this.#deferred ];

        this.#deferred.forEach((prop, index) => {
            try { 
                this[prop];
                deferred.slice(index, 1);
             }
            catch(err) { }
        });

        this.#deferred = deferred;
    }

    reactive(state) {
        this.#state = {
            ...this.#state,
            ...state
        };

        Object.keys(state).forEach(property => {
            this.#defineState(property);
        });

        this.#tryDeferred();
    }

    watch(prop, fn) {
        if(!prop || !isFn(fn)) {
            return null;
        }

        const id = uniqid.time();

        this.#watch = {
            ...this.#watch,
            [prop]: {
                ...this.#watch[prop],
                [id]: fn
            }
        };

        return () => {
            const watch = { ...this.#watch };
            delete watch[prop][id];
            this.#watch = watch;
        };
    }
}

REACTIVE_PROPERTIES.forEach(({ SYM }) => {
    ReactiveComponent.prototype[SYM] = () => {};
});

export default ReactiveComponent;
